// Auto-generated constants — DO NOT EDIT.
// Regenerate via `dotnet run --project backend/tools/BackendTypesGenerator`.
//
// Compile-time constant tables projected from their C# single-source-of-truth
// (AiCreditPackSkus, StorageTopUpPacks, StoragePlan, CreditGrants, CreditCosts,
// FrameworkCatalog, RiffWordCatalog, RecordingValidation, UploadConstraints, RequestReviewHandler,
// UserDefaults, PracticeStoryImprovEndpoint, PracticeOffBookEndpoint, PracticeCueScheduleEndpoint,
// MagicLinkRequestHandler, Support,
// MagicLinkRateLimiter). Constants are generated, never
// hand-mirrored — see
// wiki/feature-contract-reads.md §1.

import { FrameworkLens } from './enums.g.js';
import type { LiveConvoVoiceGender, Practice, RiffWordCategory } from './enums.g.js';
import type { FrameworkDescriptor } from './responses.g.js';

export const CreditPacks = {
  Starter: 'credit-pack-starter',
  Standard: 'credit-pack-standard',
  Power: 'credit-pack-power',
  Whale: 'credit-pack-whale',
} as const;

export type CreditPackSku = (typeof CreditPacks)[keyof typeof CreditPacks];

export const CreditPackCredits = {
  'credit-pack-starter': 50,
  'credit-pack-standard': 150,
  'credit-pack-power': 450,
  'credit-pack-whale': 1500,
} as const satisfies Record<CreditPackSku, number>;
export const CreditPackPriceCents = {
  'credit-pack-starter': 500,
  'credit-pack-standard': 1500,
  'credit-pack-power': 3500,
  'credit-pack-whale': 10000,
} as const satisfies Record<CreditPackSku, number>;

export const StoragePacks = {
  Hundred: 'storage-100',
  FiveHundred: 'storage-500',
  OneTerabyte: 'storage-1tb',
} as const;

export type StoragePackSku = (typeof StoragePacks)[keyof typeof StoragePacks];

export const StoragePackBytes = {
  'storage-100': 107374182400,
  'storage-500': 536870912000,
  'storage-1tb': 1099511627776,
} as const satisfies Record<StoragePackSku, number>;
export const StoragePackPriceCents = {
  'storage-100': 500,
  'storage-500': 2000,
  'storage-1tb': 3500,
} as const satisfies Record<StoragePackSku, number>;
export const PremiumMonthlyPriceCents = 1500;

export const SignupBonus = 10;
export const PremiumMonthlyCredits = 200;
export const PremiumStorageBytes = 107374182400;
export const ReviewRetentionDays = 30;

export const MaxUploadSizeBytes = 8589934592;
export const MaxUploadPartsPerSign = 16;
export const AllowedRecordingContentTypes = {
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
} as const;

export const MaxRecordingNameLength = 64;

export const MinReviewDurationSeconds = 30;
export const MaxReviewDurationSeconds = 900;
export const MaxReviewDurationSecondsByPractice = {
  TheInstrument: 900,
  ArchetypePlay: 900,
  BodyLanguagePlay: 900,
  Storytelling: 900,
  RecordAndReview: 1800,
  ActiveListening: 900,
  Riff: 900,
  Standalone: 900,
} as const satisfies Record<Practice, number>;

export const MinTalkWords = 50;
export const MaxTypedTalkWords = 1000;
export const MaxDocumentChars = 60000;
export const MaxSourceBytes = 20971520;
export const MaxPlans = 10;
export const MaxPlanNameChars = 60;

export const SupportMaxNameLength = 100;
export const SupportMaxSummaryLength = 200;
export const SupportMaxDescriptionLength = 10000;
export const SupportBotCheckAction = 'support';
export const SignInBotCheckAction = 'login';
export const PracticeChallengeTokenHeader = 'CF-Turnstile-Response';
export const PracticeChallengeAction = 'practice';

export const MasteryPlanTotalWeeks = 12;

export const PendingDeadlineSeconds = {
  CreditAccount: 120,
  PendingPurchase: 300,
  ReviewRequest: 120,
  SeriesParse: 3300,
  SeriesPlan: 2400,
} as const;

export const MinPreferredWpm = 50;
export const MaxPreferredWpm = 250;
export const PreferredWpmStep = 5;
export const DefaultPreferredWpm = 150;
export const MinRetentionKeepDays = 1;
export const MaxRetentionKeepDays = 365;
export const DefaultRetentionKeepDays = 90;
export const DefaultRecordingQuality = '720p' as const;

export const StoryImprovMinDurationSeconds = 180;
export const OffBookMinDurationSeconds = 300;
export const OffBookMaxStoryChars = 6000;
export const DefaultFirstCueSecondsByPractice = {
  TheInstrument: 10,
  ArchetypePlay: 10,
  BodyLanguagePlay: 20,
  Storytelling: 10,
  RecordAndReview: 10,
  ActiveListening: 10,
  Riff: 10,
  Standalone: 10,
} as const satisfies Record<Practice, number>;
export const LiveConvoMaxDetailsLength = 500;
export const LiveConvoMinCustomPersonaLength = 3;
export const LiveConvoMaxCustomPersonaLength = 300;
export const LiveConvoMinCustomTopicLength = 3;
export const LiveConvoMaxCustomTopicLength = 200;
export const LiveConvoMinMood = 1;
export const LiveConvoMaxMood = 5;
export const LiveConvoDefaultConvoMood = 4;
export const LiveConvoDefaultPitchMood = 3;
export const LiveConvoMinInterruptions = 1;
export const LiveConvoMaxInterruptions = 5;
export const LiveConvoDefaultInterruptions = 3;
export const LiveConvoMinCapMinutes = 2;
export const LiveConvoMaxCapMinutes = 30;
export const LiveConvoDefaultConvoCapMinutes = 5;
export const LiveConvoDefaultPitchCapMinutes = 10;
export const LiveConvoIdleCloseSeconds = 60;
export const LivePanelMinCapMinutes = 5;
export const LivePanelMaxPanelists = 3;
export const LivePanelBaseCreditsPerMinute = 2;
export const LivePanelPanelistCreditsPerMinute = 5;
export const LiveConvoSoloModelKeys = ['gpt-live-1', 'gemini-3.8-live'] as const;
export const LivePanelModelKeys = {
  'gpt-live-1': 'gpt-realtime-2.1',
  'gemini-3.8-live': 'gemini-3.8-live',
} as const satisfies Record<string, string>;
export const LiveConvoModelNames = {
  'gpt-live-1': 'OpenAI GPT',
  'gemini-3.8-live': 'Google Gemini',
  'gpt-realtime-2.1': 'OpenAI GPT',
} as const satisfies Record<string, string>;
export const LiveConvoVoiceCatalog = {
  'gpt-live-1': { vendor: 'OpenAI', mixVoiceField: 'GptLive1Voice', voices: [{ name: 'marin', gender: 'Female' }, { name: 'quartz', gender: 'Female' }, { name: 'willow', gender: 'Female' }, { name: 'gleam', gender: 'Female' }, { name: 'delta', gender: 'Female' }, { name: 'nuri', gender: 'Female' }, { name: 'juni', gender: 'Female' }, { name: 'brise', gender: 'Female' }, { name: 'bossa', gender: 'Female' }, { name: 'nira', gender: 'Female' }, { name: 'noeul', gender: 'Female' }, { name: 'harema', gender: 'Female' }, { name: 'ripple', gender: 'Male' }, { name: 'vesper', gender: 'Male' }, { name: 'stone', gender: 'Male' }, { name: 'meridian', gender: 'Male' }, { name: 'tempo', gender: 'Male' }, { name: 'beacon', gender: 'Male' }, { name: 'cinder', gender: 'Male' }, { name: 'flitz', gender: 'Male' }, { name: 'sillage', gender: 'Male' }, { name: 'shida', gender: 'Male' }] },
  'gemini-3.8-live': { vendor: 'Google', mixVoiceField: 'Gemini38LiveVoice', voices: [{ name: 'Zephyr', gender: 'Female' }, { name: 'Kore', gender: 'Female' }, { name: 'Leda', gender: 'Female' }, { name: 'Aoede', gender: 'Female' }, { name: 'Callirrhoe', gender: 'Female' }, { name: 'Autonoe', gender: 'Female' }, { name: 'Despina', gender: 'Female' }, { name: 'Erinome', gender: 'Female' }, { name: 'Laomedeia', gender: 'Female' }, { name: 'Achernar', gender: 'Female' }, { name: 'Vindemiatrix', gender: 'Female' }, { name: 'Sulafat', gender: 'Female' }, { name: 'Puck', gender: 'Male' }, { name: 'Charon', gender: 'Male' }, { name: 'Fenrir', gender: 'Male' }, { name: 'Orus', gender: 'Male' }, { name: 'Enceladus', gender: 'Male' }, { name: 'Iapetus', gender: 'Male' }, { name: 'Umbriel', gender: 'Male' }, { name: 'Algieba', gender: 'Male' }, { name: 'Algenib', gender: 'Male' }, { name: 'Rasalgethi', gender: 'Male' }, { name: 'Alnilam', gender: 'Male' }, { name: 'Schedar', gender: 'Male' }, { name: 'Gacrux', gender: 'Male' }, { name: 'Pulcherrima', gender: 'Male' }, { name: 'Achird', gender: 'Male' }, { name: 'Zubenelgenubi', gender: 'Male' }, { name: 'Sadachbia', gender: 'Male' }, { name: 'Sadaltager', gender: 'Male' }] },
  'gpt-realtime-2.1': { vendor: 'OpenAI', mixVoiceField: 'GptRealtime21Voice', voices: [{ name: 'marin', gender: 'Female' }, { name: 'coral', gender: 'Female' }, { name: 'sage', gender: 'Female' }, { name: 'shimmer', gender: 'Female' }, { name: 'alloy', gender: 'Female' }, { name: 'cedar', gender: 'Male' }, { name: 'ash', gender: 'Male' }, { name: 'ballad', gender: 'Male' }, { name: 'echo', gender: 'Male' }, { name: 'verse', gender: 'Male' }] },
} as const satisfies Record<string, { vendor: string; mixVoiceField: string; voices: readonly { name: string; gender: LiveConvoVoiceGender }[] }>;
export const LiveConvoTranscriptAiLabel = 'AI';
export const LiveConvoTranscriptUserLabel = 'User';
export const MinTargetWords = 60;
export const OutputTokensPerWord = 8;
export const RiffSecondsPerCue = 30;
export const MinRiffCues = 2;

export const DefaultCulture = 'en-AU';

export const MagicLinkTokenLifetimeSeconds = 180;
export const MagicLinkResendCooldownSeconds = 60;

export const CreditCosts = {
  ActiveListening: 1,
  AiReviewFrom: 1,
  ContentGeneration: 1,
  CueAudio: 1,
  LiveConvoGemini38LivePerMinute: 12,
  LiveConvoGptLive1PerMinute: 7,
  LivePanelBasePerMinute: 2,
  LivePanelPanelistPerMinute: 5,
  SpendConfirmThresholdCredits: 50,
} as const;

export const FRAMEWORK_CATALOG: readonly FrameworkDescriptor[] = [
  {
    lens: FrameworkLens.FourMat,
    displayName: '4MAT',
    description: 'A structural arc for explanatory / persuasive talks.',
    steps: ['Why', 'What', 'How', 'What-if'],
  },
  {
    lens: FrameworkLens.StorytellingFormula,
    displayName: 'Storytelling Formula',
    description: 'The narrative spine of a told story.',
    steps: ['Incident', 'Point', 'Linking'],
  },
  {
    lens: FrameworkLens.Vaks,
    displayName: 'VAKS',
    description: 'Sensory richness across the modalities.',
    steps: ['Visual', 'Auditory', 'Kinesthetic', 'Smell-taste'],
  },
  {
    lens: FrameworkLens.Ams,
    displayName: 'AMS',
    description: 'Rhetorical comparison devices.',
    steps: ['Analogy', 'Metaphor', 'Simile'],
  },
];

export const RIFF_WORD_CATEGORIES: readonly { category: RiffWordCategory; weight: number; description: string }[] = [
  { category: 'Everyday', weight: 60, description: 'an everyday word any adult knows from daily life — a thing, place, feeling or action that opens doors to memories, stories and feelings' },
  { category: 'Business', weight: 20, description: 'a common business word from working life — the office, money, customers, deals and teamwork — that anyone with a job can talk about' },
  { category: 'Technology', weight: 10, description: 'a common modern technology word — the devices, apps and online habits of everyday life — that anyone with a phone can talk about' },
  { category: 'Faith', weight: 5, description: 'a word from faith and spiritual life in any tradition — a practice, place or moment people of many beliefs recognise — to talk about respectfully, without preaching' },
  { category: 'Specialist', weight: 5, description: 'a term from a specialist field such as science, medicine, law or a craft that is still well known enough for a non-expert to talk about for minutes' },
];

